"use client"

import { useState } from "react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CampaignConflictModal, CampaignConflictItem } from "./campaign-conflict-modal";
import { MANAGE_ITEMS_GRADE_REQUIRED } from "./grade/grade-messages";
import { PRODUCTS_TEXTS } from "../products/constants";
import { ADD_ITEM_TEXTS } from "../utils/messages";
import { conflictsFromError, describeApiError } from "../utils/pro014-api-errors";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";
import {
  useGetPro014ItemsSummaryQuery,
  useGetPro014ItemsPaginatedQuery,
  useAddPro014CampaignItemsMutation,
  useActivatePro014CampaignItemMutation,
  useDeactivatePro014CampaignItemMutation,
} from "@/services/campaign-pro014";
import { toast } from "@/components/ui/use-toast";
import {
  Loader2,
  Package,
  Plus,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Search,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";

interface Pro014ItemsModalProps {
  campaignCode: number;
  isOpen: boolean;
  onClose: () => void;
  gradeRestricted?: boolean;
}

const PAGE_SIZE = 24;

export function Pro014ItemsModal({
  campaignCode,
  isOpen,
  onClose,
  gradeRestricted = false,
}: Pro014ItemsModalProps): JSX.Element {
  const [newItems, setNewItems] = useState("");
  const [conflicts, setConflicts] = useState<CampaignConflictItem[]>([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  // Stats globais (Total/Ativos/Inativos) — mesma fonte da tela de visualizar produtos.
  const { data: summary } = useGetPro014ItemsSummaryQuery(campaignCode, {
    skip: !isOpen,
  });

  // Lista paginada de itens (com itemCode/itemName/isActive/storeCode reais).
  const { data: itemsData, isLoading, isFetching } = useGetPro014ItemsPaginatedQuery(
    { code: campaignCode, page, limit: PAGE_SIZE, search, sort: "code_asc" },
    { skip: !isOpen },
  );

  const [addItems, { isLoading: isAdding }] = useAddPro014CampaignItemsMutation();
  const [activateItem, { isLoading: isActivating }] = useActivatePro014CampaignItemMutation();
  const [deactivateItem, { isLoading: isDeactivating }] = useDeactivatePro014CampaignItemMutation();

  const totalCount = summary?.stats?.total ?? 0;
  const activeCount = summary?.stats?.active ?? 0;
  const inactiveCount = summary?.stats?.inactive ?? 0;

  const items = itemsData?.items ?? [];
  const totalPages = itemsData?.totalPages ?? 1;

  const handleSearchChange = (value: string) => {
    setSearch(value);
    setPage(1);
  };

  const handleAddItems = async () => {
    const itemCodes = newItems
      .split(/[,;\n]/)
      .map(code => code.trim())
      .filter(code => code.length > 0);

    if (itemCodes.length === 0) {
      toast({
        title: "Atenção",
        description: "Digite pelo menos um código de produto",
        variant: "destructive",
      });
      return;
    }

    try {
      const result = await addItems({ code: campaignCode, itemCodes }).unwrap();

      toast({
        title: "Sucesso!",
        description: result.message || `${result.data.itemsAdded} item(ns) adicionado(s) com sucesso`,
        variant: "default",
      });

      setNewItems("");
    } catch (error: unknown) {
      const found: CampaignConflictItem[] = conflictsFromError(error);
      if (found.length > 0) {
        setConflicts(found);
        return;
      }

      toast({
        title: "Erro",
        description: describeApiError(error, ADD_ITEM_TEXTS.ERROR_TITLE),
        variant: "destructive",
      });
    }
  };

  const handleToggleItem = async (
    itemCode: string,
    storeCode: number,
    isActive: boolean,
  ) => {
    // Impedir desativação se restar apenas 2 ativos
    if (isActive && activeCount <= 2) {
      toast({
        title: "Ação não permitida",
        description: `Não é possível desativar. A campanha deve ter no mínimo 2 itens ativos. Atualmente: ${activeCount} ativo(s)`,
        variant: "destructive",
      });
      return;
    }

    try {
      if (isActive) {
        await deactivateItem({ code: campaignCode, itemCode, storeCode }).unwrap();
        toast({
          title: "Item desativado",
          description: `Item ${itemCode} foi desativado`,
          variant: "default",
        });
      } else {
        await activateItem({ code: campaignCode, itemCode, storeCode }).unwrap();
        toast({
          title: "Item ativado",
          description: `Item ${itemCode} foi ativado`,
          variant: "default",
        });
      }
    } catch (error: unknown) {
      const found: CampaignConflictItem[] = conflictsFromError(error);
      if (found.length > 0) {
        setConflicts(found);
        return;
      }

      toast({
        title: "Erro",
        description: describeApiError(error, PRODUCTS_TEXTS.TOGGLE_FAILED),
        variant: "destructive",
      });
    }
  };

  return (
    <>
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Package className="w-6 h-6" />
            Gerenciar Itens - Campanha #{campaignCode}
          </DialogTitle>
          <DialogDescription>
            Adicione, ative ou desative itens (SKUs) desta campanha PRO014
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-auto space-y-4">
          {/* Resumo */}
          <div className="grid grid-cols-3 gap-3">
            <div className="bg-muted rounded-lg p-3 border">
              <p className="text-xs text-muted-foreground uppercase font-medium mb-1">Total</p>
              <p className="text-xl font-semibold">{totalCount}</p>
            </div>
            <div className="bg-muted rounded-lg p-3 border">
              <p className="text-xs text-muted-foreground uppercase font-medium mb-1">Ativos</p>
              <p className="text-xl font-semibold text-green-600">{activeCount}</p>
            </div>
            <div className="bg-muted rounded-lg p-3 border">
              <p className="text-xs text-muted-foreground uppercase font-medium mb-1">Inativos</p>
              <p className="text-xl font-semibold text-gray-400">{inactiveCount}</p>
            </div>
          </div>

          {/* Adicionar Novos Itens */}
          <div className="bg-card border rounded-lg p-4 space-y-3">
            <div className="flex items-center gap-2 text-sm font-semibold">
              <Plus className="w-4 h-4" />
              Adicionar Itens
            </div>
            {gradeRestricted && (
              <Alert>
                <AlertCircle className="h-4 w-4" aria-hidden />
                <AlertDescription>{MANAGE_ITEMS_GRADE_REQUIRED}</AlertDescription>
              </Alert>
            )}
            <div className="space-y-2">
              <Label htmlFor="new-items" className="text-sm">
                Códigos de Produtos (separados por vírgula, ponto-e-vírgula ou linha)
              </Label>
              <textarea
                id="new-items"
                value={newItems}
                onChange={(e) => setNewItems(e.target.value)}
                disabled={gradeRestricted}
                placeholder="Ex: 101.625, 101.626&#10;101.627"
                className="w-full min-h-[100px] px-3 py-2 border rounded-md resize-y"
              />
              <div className="flex items-start gap-2 text-xs text-muted-foreground">
                <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>
                  Códigos duplicados serão ignorados. Mínimo de 2 itens ativos é necessário para a campanha funcionar.
                </span>
              </div>
            </div>
            <Button
              onClick={handleAddItems}
              disabled={gradeRestricted || isAdding || !newItems.trim()}
              className="w-full"
            >
              {isAdding ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Adicionando...
                </>
              ) : (
                <>
                  <Plus className="mr-2 h-4 w-4" />
                  Adicionar Itens
                </>
              )}
            </Button>
          </div>

          {/* Lista de Itens */}
          <div className="bg-card border rounded-lg p-4 space-y-3">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2 text-sm font-semibold">
                <Package className="w-4 h-4" />
                Itens da Campanha ({totalCount})
              </div>
              <div className="relative flex-1 max-w-xs">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  value={search}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  placeholder="Buscar por código ou nome..."
                  className="pl-8 h-9"
                />
              </div>
            </div>

            {isLoading || isFetching ? (
              <div className="flex items-center justify-center h-40">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : items.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                {search ? "Nenhum item encontrado com esse código" : "Nenhum item cadastrado ainda"}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 max-h-[300px] overflow-y-auto">
                {items.map((item) => (
                  <div
                    key={`${item.itemCode}-${item.storeCode}`}
                    className={`flex items-center justify-between gap-2 p-2 rounded-lg border-2 transition-all ${
                      item.isActive
                        ? "border-green-200 bg-green-50 dark:bg-green-950/20"
                        : "border-gray-200 bg-gray-50 dark:bg-gray-950/20 opacity-60"
                    }`}
                  >
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      {item.isActive ? (
                        <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0" />
                      ) : (
                        <XCircle className="w-4 h-4 text-gray-400 flex-shrink-0" />
                      )}
                      <div className="flex flex-col min-w-0">
                        <span className="text-sm font-mono truncate">{item.itemCode}</span>
                        <span className="text-xs text-muted-foreground truncate">
                          {item.itemName?.trim() || "—"}
                        </span>
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleToggleItem(item.itemCode, item.storeCode, item.isActive)}
                      disabled={isActivating || isDeactivating || (item.isActive && activeCount <= 2)}
                      className="h-6 w-6 p-0"
                      title={
                        item.isActive && activeCount <= 2
                          ? "Mínimo de 2 itens ativos"
                          : item.isActive
                          ? "Desativar item"
                          : "Ativar item"
                      }
                    >
                      {item.isActive ? (
                        <XCircle className={`w-3.5 h-3.5 ${activeCount <= 2 ? 'text-gray-400' : 'text-red-500'}`} />
                      ) : (
                        <CheckCircle2 className="w-3.5 h-3.5 text-green-500" />
                      )}
                    </Button>
                  </div>
                ))}
              </div>
            )}

            {/* Paginação */}
            {totalPages > 1 && (
              <div className="flex items-center justify-end gap-2 pt-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1 || isFetching}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="text-sm text-muted-foreground">
                  Página {page} de {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages || isFetching}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            )}
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-4 border-t">
          <Button variant="outline" onClick={onClose}>
            Fechar
          </Button>
        </div>
      </DialogContent>
    </Dialog>

    <CampaignConflictModal
      isOpen={conflicts.length > 0}
      conflicts={conflicts}
      onClose={() => setConflicts([])}
      onGoBack={() => setConflicts([])}
    />
    </>
  );
}
